import apiClient from './api';
import { convertPeseeToTicketData } from '@/utils/ticketDataConverter';
import { getDisplayPeseeCode } from '@/utils/ticketSequences';

export interface HistoryPesee {
  id: string;
  reference: string;
  référence?: string; // Champ avec accent pour compatibilité API
  sequence_generale?: number | null;
  sequence_annuelle?: number | null;
  sequence_mensuelle?: number | null;
  sequence_journaliere?: number | null;
  date_heure: string;
  statut: string;
  statut_display: string;
  processus?: string;
  type_pesee: string;
  type_pesee_display: string;
  poids_brut: number;
  poids_tare: number;
  poids_net: number;
  provenance: string;
  destination: string;
  numero_lot: string;
  observations: string;
  en_stock: boolean;
  camion_immatriculation: string;
  chauffeur_nom: string;
  produit_nom: string;
  fournisseur_nom: string;
  client_nom: string;
  exportateur_nom: string;
  created_at: string;
  updated_at: string;
  // Informations détaillées des acteurs
  camion_info?: {
    id: number;
    immatriculation: string;
    marque: string;
    modele: string;
    capacite: number;
    proprietaire: string;
  } | null;
  chauffeur_info?: {
    id: number;
    nom: string;
    prenom: string;
    telephone: string;
    email: string;
  } | null;
  produit_info?: {
    id: number;
    nom: string;
    code: string;
    type: string;
    type_display: string;
    unite: string;
    categorie: string;
  } | null;
  // Propriétés de compatibilité pour l'export
  produit?: string;
  poids_theorique?: number;
  camion?: {
    id: number;
    immatriculation: string;
    marque: string;
    modele: string;
    proprietaire: string;
  };
  remorque_info?: {
    id: number;
    immatriculation: string;
    marque: string;
    modele: string;
  };
  remorque?: {
    id: number;
    immatriculation: string;
    marque: string;
    modele: string;
  };
  chauffeur?: {
    id: number;
    nom: string;
    prenom: string;
    telephone: string;
    email: string;
  };
  convoyeur_info?: {
    id: number;
    nom: string;
    prenom: string;
    telephone: string;
    email: string;
  };
  convoyeur?: {
    id: number;
    nom: string;
    prenom: string;
    telephone: string;
    email: string;
  };
  client_info?: {
    id: number;
    nom: string;
    code: string;
    type: string;
    telephone: string;
    email: string;
    secteur: string;
    adresse: string;
  };
  client?: {
    id: number;
    nom: string;
    code: string;
    type: string;
    telephone: string;
    email: string;
    secteur: string;
    adresse: string;
  };
  fournisseur_info?: {
    id: number;
    nom: string;
    code: string;
    cooperative: string;
    contact: string;
    email: string;
    adresse: string;
  };
  fournisseur?: {
    id: number;
    nom: string;
    code: string;
    cooperative: string;
    contact: string;
    email: string;
    adresse: string;
  };
  transporteur_info?: {
    id: number;
    nom: string;
    code: string;
  };
  transporteur?: {
    id: number;
    nom: string;
    code: string;
  };
  exportateur_info?: {
    id: number;
    nom: string;
    code: string;
    type: string;
    telephone: string;
    email: string;
    pays: string;
    zone: string;
  };
  exportateur?: {
    id: number;
    nom: string;
    code: string;
    type: string;
    telephone: string;
    email: string;
    pays: string;
    zone: string;
  };
  // Propriétés de création/modification
  created_by?: {
    id: number;
    username: string;
    nom: string;
  };
  updated_by?: {
    id: number;
    username: string;
    nom: string;
  };
  // Propriétés supplémentaires pour l'export
  transporteur_nom?: string;
  convoyeur_nom?: string;
  
  // Nouveaux champs d'analyse qualité
  quality_level?: string;
  grainnage?: string;
  quality?: number;
  humidite?: number;
  
  // Objet analyse_qualite complet (depuis l'API)
  analyse_qualite?: {
    grainnage?: string;
    humidite?: number;
    quality?: number;
    quality_level?: string;
    quality_decision?: string;
    quality_score?: number;
    observations?: string;
    analysed_by_username?: string;
    analysed_at?: string;
  };
  
  // Champs de comptage des sacs et poids
  nb_sacs_declares?: number;
  nb_sacs_sains?: number;
  nb_sacs_recond?: number;
  nb_sacs_humides?: number;
  nb_sacs_dechires?: number;
  nb_sacs_rejetes?: number;
  nb_sacs_acceptes?: number;
  poids_acceptes?: number;
  poids_sac?: number;
}

export interface HistoryFilters {
  search?: string;
  produit?: string;
  statut?: string;
  type_pesee?: string;
  date_from?: string;
  date_to?: string;
  vehicule?: string; // ID du véhicule
  type_vehicule?: string; // Type de véhicule (Vehicule, Fourchette, etc.)
  remorque?: string; // ID de la remorque
  chauffeur?: string; // ID du chauffeur
  convoyeur?: string; // ID du convoyeur
  client?: string; // ID du client
  fournisseur?: string; // ID du fournisseur
  transporteur?: string; // ID du transporteur
  exportateur?: string; // ID de l'exportateur
  magasin?: string; // ID du magasin
  conditionnement?: string; // ID du conditionnement
  provenance?: string; // Lieu de provenance
  page?: number;
  page_size?: number;
}

export interface HistoryResponse {
  success: boolean;
  message: string;
  data: HistoryPesee[];
  count?: number;
  next?: string;
  previous?: string;
}

class HistoryService {
  /**
   * Récupérer toutes les pesées avec filtres
   */
  async getPesees(filters: HistoryFilters = {}): Promise<HistoryResponse> {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.produit) params.append('produit', filters.produit);
      if (filters.statut) params.append('statut', filters.statut);
      if (filters.type_pesee) params.append('type_pesee', filters.type_pesee);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.vehicule) params.append('camion', filters.vehicule);
      if (filters.type_vehicule) params.append('type_vehicule', filters.type_vehicule);
      if (filters.remorque) params.append('remorque', filters.remorque);
      if (filters.chauffeur) params.append('chauffeur', filters.chauffeur);
      if (filters.convoyeur) params.append('convoyeur', filters.convoyeur);
      if (filters.client) params.append('client', filters.client);
      if (filters.fournisseur) params.append('fournisseur', filters.fournisseur);
      if (filters.transporteur) params.append('transporteur', filters.transporteur);
      if (filters.exportateur) params.append('exportateur', filters.exportateur);
      if (filters.magasin) params.append('magasin', filters.magasin);
      if (filters.conditionnement) params.append('conditionnement', filters.conditionnement);
      if (filters.provenance) params.append('provenance', filters.provenance);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.page_size) params.append('page_size', filters.page_size.toString());

      // Ajouter les paramètres pour récupérer les relations et l'analyse qualité
      params.append('include_relations', 'true');
      params.append('expand', 'camion,chauffeur,produit,client,fournisseur,exportateur,analyse_qualite');
      
      const response = await apiClient.get(`/weighing/api/pesees/?${params.toString()}`);
      
      // Gérer la pagination Django REST Framework
      if (response.data && response.data.results && Array.isArray(response.data.results)) {
        return {
          success: true,
          message: 'Pesées récupérées avec succès',
          data: response.data.results,
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous
        };
      } else if (Array.isArray(response.data)) {
        return {
          success: true,
          message: 'Pesées récupérées avec succès',
          data: response.data
        };
      } else {
        return {
          success: false,
          message: 'Structure de réponse inattendue',
          data: []
        };
      }
    } catch (error: any) {
      console.error('Erreur lors de la récupération des pesées:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des pesées');
    }
  }

  /**
   * Récupérer une pesée par ID
   */
  async getPeseeById(id: string): Promise<HistoryPesee> {
    try {
      const response = await apiClient.get(`/weighing/api/pesees/${id}/`);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la récupération de la pesée:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération de la pesée');
    }
  }

  /**
   * Supprimer définitivement une pesée
   */
  async deletePesee(id: string): Promise<{ success: boolean; message: string }> {
    try {
      await apiClient.delete(`/weighing/api/pesees/${id}/`);
      return {
        success: true,
        message: 'Pesée supprimée avec succès'
      };
    } catch (error: any) {
      console.error('Erreur lors de la suppression de la pesée:', error);
      const data = error.response?.data;
      const apiMessage =
        (typeof data === 'string' ? data : null) ||
        data?.message ||
        data?.detail ||
        data?.error ||
        error.message;
      throw new Error(apiMessage || 'Erreur lors de la suppression de la pesée');
    }
  }

  /**
   * Refuser une pesée
   */
  async rejectPesee(id: string, motif: string): Promise<{ success: boolean; message: string }> {
    try {
      await apiClient.post(`/weighing/api/pesees/${id}/reject/`, {
        motif: motif
      });
      return {
        success: true,
        message: 'Pesée refusée avec succès'
      };
    } catch (error: any) {
      console.error('Erreur lors du refus de la pesée:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors du refus de la pesée');
    }
  }

  /**
   * Ajouter une pesée au stock
   */
  async addToStock(id: string): Promise<{ success: boolean; message: string }> {
    try {
      await apiClient.post(`/weighing/api/pesees/${id}/add-to-stock/`);
      return {
        success: true,
        message: 'Pesée ajoutée au stock avec succès'
      };
    } catch (error: any) {
      console.error('Erreur lors de l\'ajout au stock:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de l\'ajout au stock');
    }
  }

  /**
   * Continuer une pesée (rediriger vers la page de pesée)
   */
  getContinuePeseeUrl(id: string): string {
    return `/nouvelle-pesee?continue=${id}`;
  }

  /**
   * Récupérer la référence d'une pesée (gère les deux formats)
   */
  getPeseeReference(pesee: HistoryPesee): string {
    return getDisplayPeseeCode(pesee);
  }

  /**
   * Convertit une pesée en WeighingTicketData pour les actions de ticket
   * Utilise maintenant la fonction centralisée convertPeseeToTicketData
   */
  convertPeseeToWeighingTicketData(pesee: HistoryPesee | Record<string, unknown>): any {
    return convertPeseeToTicketData(pesee);
  }

  /**
   * Recharge la pesée depuis l'API puis convertit pour l'impression.
   * Évite d'imprimer des données de liste obsolètes / incomplètes.
   */
  async convertPeseeToWeighingTicketDataFresh(pesee: HistoryPesee): Promise<any> {
    try {
      const peseeId =
        typeof pesee.id === 'string' ? parseInt(pesee.id, 10) : Number(pesee.id);
      if (!Number.isNaN(peseeId)) {
        const response = await apiClient.get(`/weighing/api/pesees/${peseeId}/`);
        const payload = response?.data;
        const fresh =
          payload?.data && typeof payload.data === 'object'
            ? payload.data
            : payload;
        if (fresh && (fresh.reference || fresh.poids_net != null || fresh.id)) {
          return convertPeseeToTicketData({ ...pesee, ...fresh });
        }
      }
    } catch (error) {
      console.warn('Impossible de recharger la pesée avant impression, données locales utilisées', error);
    }
    return convertPeseeToTicketData(pesee);
  }

  /**
   * Récupérer les données de refoulement pour une pesée
   */
  async getRefoulementData(peseeId: string): Promise<any> {
    try {
      // Essayer d'abord avec pesee__reference (comme défini dans filterset_fields)
      let response = await apiClient.get(`/weighing/api/refoulements/?pesee__reference=${peseeId}`);
     
      if (response.data && response.data.results && response.data.results.length > 0) {
        return response.data.results[0];
      }
      
      // Si pas trouvé, essayer avec pesee (ID numérique)
    
      response = await apiClient.get(`/weighing/api/refoulements/?pesee=${peseeId}`);

      
      if (response.data && response.data.results && response.data.results.length > 0) {
       
        return response.data.results[0];
      }
      
      return null;
    } catch (error: any) {
      console.error('❌ Erreur lors de la récupération des données de refoulement:', error);
      return null;
    }
  }

  /**
   * Obtenir les actions disponibles selon le statut
   */
  getAvailableActions(
    statut: string,
    enStock: boolean = false,
    pesee?: { poids_brut?: number | string | null; poids_tare?: number | string | null },
  ): string[] {
    const actions: string[] = ['voir_detail', 'voir_acteurs', 'supprimer'];
    const hasWeight = (value: number | string | null | undefined) => {
      if (value === null || value === undefined || value === '') return false;
      const parsed = parseFloat(String(value));
      return !Number.isNaN(parsed) && parsed > 0;
    };
    const hasBrut = hasWeight(pesee?.poids_brut);
    const hasTare = hasWeight(pesee?.poids_tare);
    const isIncomplete = !hasBrut || !hasTare;

    switch (statut) {
      case 'EN_COURS':
      case 'EN_ATTENTE_TARE':
      case 'BRUT_ENREGISTRE':
        actions.push('continuer');
        break;

      case 'TARE_ENREGISTREE':
        // Parcours inversé / tare seule : la pesée n'est pas terminée
        if (isIncomplete) {
          actions.push('continuer');
        } else {
          actions.push('imprimer_ticket', 'exporter_pdf', 'envoyer_email');
          actions.push('continuer');
        }
        break;

      case 'COMPLETEE':
        actions.push('imprimer_ticket', 'exporter_pdf', 'envoyer_email');
        if (!enStock) {
          actions.push('ajouter_stock');
        }
        break;

      case 'ACHEVEE':
      case 'REFOULEE':
        actions.push('imprimer_ticket', 'exporter_pdf', 'envoyer_email');
        break;
    }

    if (isPeseeEditable(statut)) {
      actions.push('modifier');
    }

    return actions;
  }
}

/** Statuts pour lesquels la modification est interdite */
export const NON_EDITABLE_PESSE_STATUSES = ['REFOULEE', 'ANNULEE'] as const;

/**
 * Statuts finalisés : modification réservée aux rôles admin
 * (PLATFORM_*, TENANT_ADMIN/OWNER, SITE_ADMIN)
 */
export const ADMIN_ONLY_EDIT_PESSE_STATUSES = ['COMPLETEE', 'ACHEVEE'] as const;

/** Statuts pour lesquels la suppression est interdite */
export const NON_DELETABLE_PESSE_STATUSES = ['COMPLETEE', 'ACHEVEE', 'REFOULEE', 'ANNULEE'] as const;

export function isPeseeEditable(statut: string): boolean {
  return !NON_EDITABLE_PESSE_STATUSES.includes(statut as typeof NON_EDITABLE_PESSE_STATUSES[number]);
}

/** Pesée complète / achevée : nécessite un droit admin pour être modifiée */
export function requiresAdminToEditPesee(statut: string): boolean {
  return ADMIN_ONLY_EDIT_PESSE_STATUSES.includes(
    statut as typeof ADMIN_ONLY_EDIT_PESSE_STATUSES[number]
  );
}

export function isPeseeDeletable(statut: string): boolean {
  return !NON_DELETABLE_PESSE_STATUSES.includes(statut as typeof NON_DELETABLE_PESSE_STATUSES[number]);
}

export default new HistoryService();
